<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue"
import { dragPosition, resizeDimensions } from "../utils/window_geometry"

const props = defineProps({
  windowState: { type: Object, required: true },
  labels: { type: Object, default: () => ({}) },
})

const emit = defineEmits([
  "close",
  "minimize",
  "toggle-maximize",
  "focus",
  "update-geometry",
])

const label = (key, fallback) => props.labels[key] || fallback
const frameId = computed(() => `content_window_${props.windowState.id}`)
const frameHost = ref(null)

function frameLoadingHtml() {
  return `<div class="content-window-loading">
    <i class="bi bi-arrow-repeat content-window-spinner"></i>
    ${label("loading", "Loading...")}
  </div>`
}

function ensureTurboFrame() {
  const host = frameHost.value
  if (!host) return

  let frame = host.querySelector("turbo-frame")
  const nextId = frameId.value
  const nextUrl = props.windowState.url
  const creating = !frame

  if (!frame) {
    frame = document.createElement("turbo-frame")
    host.appendChild(frame)
  }

  const urlChanged = frame.getAttribute("src") !== nextUrl

  if (frame.id !== nextId) frame.id = nextId
  if (frame.getAttribute("loading") !== "lazy") frame.setAttribute("loading", "lazy")

  if (creating || urlChanged) {
    frame.innerHTML = frameLoadingHtml()
    frame.setAttribute("src", nextUrl)
  }
}

const DRAG_THRESHOLD = 5

const dragging = ref(false)
const resizing = ref(false)
const resizeDirection = ref("")
const headerPressed = ref(false)
const dragStart = ref({ x: 0, y: 0, winX: 0, winY: 0 })
const resizeStart = ref({ x: 0, y: 0, width: 0, height: 0, winX: 0, winY: 0 })

const windowStyle = computed(() => ({
  left: `${props.windowState.x}px`,
  top: `${props.windowState.y}px`,
  width: `${props.windowState.width}px`,
  height: `${props.windowState.height}px`,
  zIndex: props.windowState.zIndex,
}))

function onHeaderMouseDown(event) {
  if (event.button !== 0 || event.target.closest(".content-window-controls")) return
  emit("focus")
  headerPressed.value = true
  dragging.value = false
  dragStart.value = {
    x: event.clientX,
    y: event.clientY,
    winX: props.windowState.x,
    winY: props.windowState.y,
  }
  event.preventDefault()
}

function onHeaderClick() {
  if (props.windowState.maximized) {
    emit("minimize")
  } else {
    emit("toggle-maximize")
  }
}

function onResizeMouseDown(event, direction) {
  if (event.button !== 0 || props.windowState.maximized) return
  emit("focus")
  resizing.value = true
  resizeDirection.value = direction
  resizeStart.value = {
    x: event.clientX,
    y: event.clientY,
    width: props.windowState.width,
    height: props.windowState.height,
    winX: props.windowState.x,
    winY: props.windowState.y,
  }
  event.preventDefault()
  event.stopPropagation()
}

function onMouseMove(event) {
  if (headerPressed.value && !dragging.value) {
    const deltaX = event.clientX - dragStart.value.x
    const deltaY = event.clientY - dragStart.value.y
    if (Math.abs(deltaX) > DRAG_THRESHOLD || Math.abs(deltaY) > DRAG_THRESHOLD) {
      dragging.value = true
    }
  }

  if (dragging.value) {
    const deltaX = event.clientX - dragStart.value.x
    const deltaY = event.clientY - dragStart.value.y
    const position = dragPosition(
      dragStart.value.winX,
      dragStart.value.winY,
      deltaX,
      deltaY,
      props.windowState.width,
      props.windowState.height
    )
    emit("update-geometry", position)
  }

  if (resizing.value) {
    const deltaX = event.clientX - resizeStart.value.x
    const deltaY = event.clientY - resizeStart.value.y
    const geometry = resizeDimensions(
      resizeStart.value.width,
      resizeStart.value.height,
      resizeStart.value.winX,
      resizeStart.value.winY,
      deltaX,
      deltaY,
      resizeDirection.value
    )
    emit("update-geometry", geometry)
  }
}

function onMouseUp() {
  if (headerPressed.value && !dragging.value) {
    onHeaderClick()
  }

  headerPressed.value = false
  dragging.value = false
  resizing.value = false
  resizeDirection.value = ""
}

onMounted(() => {
  document.addEventListener("mousemove", onMouseMove)
  document.addEventListener("mouseup", onMouseUp)
  nextTick(ensureTurboFrame)
})

onUnmounted(() => {
  document.removeEventListener("mousemove", onMouseMove)
  document.removeEventListener("mouseup", onMouseUp)
})

watch(() => [props.windowState.id, props.windowState.url], () => {
  nextTick(ensureTurboFrame)
})
</script>

<template>
  <div
    class="content-window"
    :class="{ 'is-maximized': windowState.maximized }"
    :style="windowStyle"
    :data-window-id="windowState.id"
    @mousedown="emit('focus')"
  >
    <header class="content-window-header" @mousedown="onHeaderMouseDown">
      <span class="content-window-title">{{ windowState.title }}</span>
      <div class="content-window-controls">
        <button
          type="button"
          class="content-window-btn content-window-btn-minimize"
          data-bs-toggle="tooltip"
          :data-bs-title="label('minimize', 'Minimize')"
          :aria-label="label('minimize', 'Minimize')"
          @click.stop="emit('minimize')"
        >
          <i class="bi bi-dash-lg" />
        </button>
        <button
          type="button"
          class="content-window-btn content-window-btn-maximize"
          data-bs-toggle="tooltip"
          :data-bs-title="label(windowState.maximized ? 'restore' : 'maximize', windowState.maximized ? 'Restore' : 'Maximize')"
          :aria-label="label(windowState.maximized ? 'restore' : 'maximize', windowState.maximized ? 'Restore' : 'Maximize')"
          @click.stop="emit('toggle-maximize')"
        >
          <i :class="windowState.maximized ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'" />
        </button>
        <button
          type="button"
          class="content-window-btn content-window-btn-close"
          data-bs-toggle="tooltip"
          :data-bs-title="label('close', 'Close')"
          :aria-label="label('close', 'Close')"
          @click.stop="emit('close')"
        >
          <i class="bi bi-x-lg" />
        </button>
      </div>
    </header>

    <div ref="frameHost" class="content-window-body" />

    <template v-if="!windowState.maximized">
      <div class="content-window-resize content-window-resize-n" @mousedown="onResizeMouseDown($event, 'n')" />
      <div class="content-window-resize content-window-resize-s" @mousedown="onResizeMouseDown($event, 's')" />
      <div class="content-window-resize content-window-resize-e" @mousedown="onResizeMouseDown($event, 'e')" />
      <div class="content-window-resize content-window-resize-w" @mousedown="onResizeMouseDown($event, 'w')" />
      <div class="content-window-resize content-window-resize-ne" @mousedown="onResizeMouseDown($event, 'ne')" />
      <div class="content-window-resize content-window-resize-nw" @mousedown="onResizeMouseDown($event, 'nw')" />
      <div class="content-window-resize content-window-resize-se" @mousedown="onResizeMouseDown($event, 'se')" />
      <div class="content-window-resize content-window-resize-sw" @mousedown="onResizeMouseDown($event, 'sw')" />
    </template>
  </div>
</template>
